import React from "react";
import { gql } from "apollo-boost";

import StatusBadge from "designV2/components/StatusBadge";
import { graphqlUrl } from "utils/config";
import SettingsList, { humanize } from "superAdminViews/setting/SettingsList";

const GET = gql`
  query getReqCarTypesByAdmin($limit: Int, $skip: Int) {
    getReqCarTypesByAdmin(pagination: { limit: $limit, skip: $skip }) {
      _id
      name
      logoUrl
      tripType
      increasePricePercent
      DistanceBasePricePerKM
      PerMinute
      WaitTimePricePerMin
      BookingFee
      BaseFare
      maximumPassengersCount
      maximumWeight
      maximumVolume
      description
    }
  }
`;

const COUNT = gql`
  query getReqCarTypesByAdminCount {
    getReqCarTypesByAdminCount
  }
`;

const UPDATE = gql`
  mutation updateReqCarTypeByAdmin(
    $id: ID
    $increasePricePercent: Int!
    $DistanceBasePricePerKM: Float!
    $PerMinute: Float!
    $WaitTimePricePerMin: Float!
    $BookingFee: Float!
    $BaseFare: Float!
    $maximumPassengersCount: Int!
    $maximumWeight: Int!
    $maximumVolume: Int!
    $description: String!
  ) {
    updateReqCarTypeByAdmin(
      filters: { _id: $id }
      input: {
        increasePricePercent: $increasePricePercent
        DistanceBasePricePerKM: $DistanceBasePricePerKM
        PerMinute: $PerMinute
        WaitTimePricePerMin: $WaitTimePricePerMin
        BookingFee: $BookingFee
        BaseFare: $BaseFare
        maximumPassengersCount: $maximumPassengersCount
        maximumWeight: $maximumWeight
        maximumVolume: $maximumVolume
        description: $description
      }
    ) {
      _id
      name
      increasePricePercent
      DistanceBasePricePerKM
      PerMinute
      WaitTimePricePerMin
      BookingFee
      BaseFare
      maximumPassengersCount
      maximumWeight
      maximumVolume
      description
    }
  }
`;

const money = (value) => (value === null || value === undefined ? "—" : `$${value}`);

const FIELDS = [
  {
    name: "BaseFare",
    label: "Base fare",
    type: "number",
    step: "0.01",
    required: true,
    hint: "Charged the moment the trip starts, before any distance.",
  },
  { name: "BookingFee", label: "Booking fee", type: "number", step: "0.01", required: true },
  {
    name: "DistanceBasePricePerKM",
    label: "Per kilometre",
    type: "number",
    step: "0.01",
    required: true,
  },
  { name: "PerMinute", label: "Per minute", type: "number", step: "0.01", required: true },
  {
    name: "WaitTimePricePerMin",
    label: "Waiting, per minute",
    type: "number",
    step: "0.01",
    required: true,
  },
  {
    name: "increasePricePercent",
    label: "Surge, %",
    type: "number",
    required: true,
    hint: "Added on top of everything above. 0 means no surge.",
  },
  {
    name: "maximumPassengersCount",
    label: "Maximum passengers",
    type: "number",
    required: true,
  },
  { name: "maximumWeight", label: "Maximum weight, kg", type: "number", required: true },
  { name: "maximumVolume", label: "Maximum volume, litres", type: "number", required: true },
  { name: "description", label: "Description", type: "textarea", required: true },
];

/**
 * CarTypes - what each class of vehicle costs to ride in.
 *
 * The names, logos and trip types come from the platform and cannot be
 * added or removed here; the fares can. Every number on this screen
 * ends up on a customer's receipt, so the row shows the fare in full
 * rather than hiding it behind the edit dialog.
 */
const CarTypes = () => (
  <SettingsList
    title="Car types"
    description="Fares and limits for each class of vehicle. Changes apply to the next trip booked."
    singular="car type"
    list={{ query: GET, key: "getReqCarTypesByAdmin" }}
    count={{ query: COUNT, key: "getReqCarTypesByAdminCount" }}
    update={{
      mutation: UPDATE,
      variables: (values, row) => ({
        id: row._id,
        increasePricePercent: parseInt(values.increasePricePercent, 10),
        DistanceBasePricePerKM: parseFloat(values.DistanceBasePricePerKM),
        PerMinute: parseFloat(values.PerMinute),
        WaitTimePricePerMin: parseFloat(values.WaitTimePricePerMin),
        BookingFee: parseFloat(values.BookingFee),
        BaseFare: parseFloat(values.BaseFare),
        maximumPassengersCount: parseInt(values.maximumPassengersCount, 10),
        maximumWeight: parseInt(values.maximumWeight, 10),
        maximumVolume: parseInt(values.maximumVolume, 10),
        description: values.description.trim(),
      }),
    }}
    columns={[
      {
        key: "name",
        label: "Car type",
        width: "2fr",
        render: (row) => (
          <span className="settingsSwatch">
            {row.logoUrl && (
              <img className="settingsThumb" src={`${graphqlUrl}${row.logoUrl}`} alt="" />
            )}
            <span className="settingsPrimaryCell">
              <strong>{row.name}</strong>
              <span>{row.maximumPassengersCount} seats</span>
            </span>
          </span>
        ),
      },
      {
        key: "tripType",
        label: "Used for",
        render: (row) => <StatusBadge status="neutral" label={humanize(row.tripType)} />,
      },
      { key: "BaseFare", label: "Base fare", render: (row) => money(row.BaseFare) },
      {
        key: "DistanceBasePricePerKM",
        label: "Per km",
        render: (row) => money(row.DistanceBasePricePerKM),
      },
      { key: "PerMinute", label: "Per min", render: (row) => money(row.PerMinute) },
      {
        key: "increasePricePercent",
        label: "Surge",
        render: (row) => `${row.increasePricePercent || 0}%`,
      },
    ]}
    fields={FIELDS}
    toValues={(row) => ({
      BaseFare: row.BaseFare,
      BookingFee: row.BookingFee,
      DistanceBasePricePerKM: row.DistanceBasePricePerKM,
      PerMinute: row.PerMinute,
      WaitTimePricePerMin: row.WaitTimePricePerMin,
      increasePricePercent: row.increasePricePercent,
      maximumPassengersCount: row.maximumPassengersCount,
      maximumWeight: row.maximumWeight,
      maximumVolume: row.maximumVolume,
      description: row.description || "",
    })}
  />
);

export default CarTypes;
